import React, {FC} from 'react';
import { Edit, DoNotDisturb } from '@mui/icons-material';
import { PendingInventoryActivityVO } from "../../../types/model/valueObject/pendinginventoryactivity/PendingInventoryActivityVO";
import { dialogs } from "./SupplierPendingInventoryActivityDialogWrapper";
import { reportWarnings } from '../../constants/report-verbiage';
import { ReportDialogSetterProps } from "../../../interfaces";
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuItemLabel,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem,
  CollapsableCardHeaderItemWithWarning
} from "../../cardComponents";
import { useMenu } from "../../../hooks/report/useMenu";

type SupplierPendingInventoryActivityCardListItemProps = {
  item: PendingInventoryActivityVO;
  expanded?: boolean;
  onToggleExpand: () => void;
  onEdit?: (item: PendingInventoryActivityVO) => void;
  onOpenDialog: (dialog: ReportDialogSetterProps<PendingInventoryActivityVO>) => void;
}

/**
 * Component that displays all information for a single supplier pending inventory activity item
 * @param item the PendingInventoryActivityVO to display
 * @param expanded
 * @param onToggleExpand
 * @param onEdit
 * @param onOpenDialog
 * @constructor
 */
const SupplierPendingInventoryActivityCardListItem: FC<SupplierPendingInventoryActivityCardListItemProps> = ({
  item,
  expanded,
  onToggleExpand,
  onEdit,
  onOpenDialog
}) => {
  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();
  const isPending = item?.status === 'PENDING';

  const handleEditClick = () => {
    handleClose();
    onEdit?.(item);
  };

  const handleAction = (dialog: string) => {
    handleClose();
    onOpenDialog({ item, dialog })
  }

  return (
    <CollapsableCardContainer expanded={expanded}>
        <CollapsableCardClickableContainer onClick={onToggleExpand}>
          <CollapsableCardHeaderContainer>
            <CollapsableCardHeaderItem value={item?.eventTypeLabel} label={'Event Type'} />
            <CollapsableCardHeaderItem value={item?.eventDatetime} label={'Event Date/Time'} />
            <CollapsableCardHeaderItemWithWarning
              label={'CONTAINER'}
              value={item?.containerNumber}
              showWarning={!!(item?.containerNumber && !item?.containerNumberValid)}
              warningMessage={reportWarnings.containerNumberIsoWarning}
            />
            <CollapsableCardHeaderItem value={item?.trailerNumber} label={'Trailer'} />
            <CollapsableCardHeaderItem value={item?.chassisNumber} label={'Chassis'} />
            <CollapsableCardHeaderItem value={item?.bookingNumber} label={'Booking Number'} />
            <CollapsableCardHeaderActionsContainer>
              <CollapsableCardExpandIconButton expanded={expanded ?? false} />
            </CollapsableCardHeaderActionsContainer>
          </CollapsableCardHeaderContainer>
        </CollapsableCardClickableContainer>
        <CollapsableCardCollapseContainer in={expanded}>
          <CollapsableCardBodyContainer>
            <CollapsableCardBodyItem value={item?.chassisLicensePlateNumber} label={'Chassis License Plate'} />
            <CollapsableCardBodyItem value={item?.assetSize} label={'Size'} />
            <CollapsableCardBodyItem value={item?.sealNumber} label={'Seal'} />
            <CollapsableCardBodyItem value={item?.assetType} label={'Type'} />
            <CollapsableCardBodyItem value={`${item?.driverFirstName} ${item?.driverLastName}`} label={'Driver'} />
            <CollapsableCardBodyItem value={item?.driverLicenseNumber} label={'License'} />

            <CollapsableCardBodyItem value={item?.truckLicensePlateNumber} label={'Truck License Plate Number'} />
            <CollapsableCardBodyItem value={item?.scacCode} label={'SCAC Code'} />
            <CollapsableCardBodyItem value={item?.statusLabel} label={'Status'} />
            <CollapsableCardBodyItem value={item?.validationErrorCodeLabel} label={'Error Code'} />
            <CollapsableCardBodyItem value={item?.validationErrorMessage} label={'Error Message'} />
            <CollapsableCardBodyItem value={item?.notes} label={'Notes'} />
            <CollapsableCardBodyActionsContainer>
              <ActionsMenuOpenButton onClick={handleClick} />
              <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
                <ActionsMenuItem onClick={handleEditClick} disabled={!isPending}>
                  <Edit fontSize="small" color="primary" />
                  <ActionsMenuItemLabel>Edit</ActionsMenuItemLabel>
                </ActionsMenuItem>
                <ActionsMenuItem onClick={() => handleAction(dialogs.DISMISS)} disabled={item?.status !== 'PENDING'}>
                  <DoNotDisturb fontSize="small" color="primary" />
                  <ActionsMenuItemLabel>Dismiss</ActionsMenuItemLabel>
                </ActionsMenuItem>
              </ActionsMenu>
            </CollapsableCardBodyActionsContainer>
          </CollapsableCardBodyContainer>
        </CollapsableCardCollapseContainer>
      </CollapsableCardContainer>
  );
};

export { SupplierPendingInventoryActivityCardListItem };
